// FIXED: MoreActionsMenu.jsx - Redirect to ChangeRequirementsPage on Edit
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  ClickAwayListener,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  Alert,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  AccountTree as ParentIcon,
  Timeline as StoryPointIcon,
  CallSplit as SplitIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  Warning as WarningIcon,
  Edit as EditIcon
} from '@mui/icons-material';

import HierarchicalParentMenu from './parent';
import MoveWorkItemMenu from './MoveItem';
import SplitWorkItemDialog from './SplitItem';
import StoryPointEstimateMenu from './StroyPoints';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MoreActionsMenu = ({ 
  issue, 
  onAssigneeChange, 
  projectId,
  teamMembers = [], 
  getInitials,
  onDelete,
  onAction,
  onParentChange,
  projectKey,
  onMove,
  labels = []
}) => {
  const navigate = useNavigate();

  // Menu states
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isAssigneeSubmenuOpen, setIsAssigneeSubmenuOpen] = useState(false);
  const [isParentSubmenuOpen, setIsParentSubmenuOpen] = useState(false);
  const [isStoryPointSubmenuOpen, setIsStoryPointSubmenuOpen] = useState(false);
  const [isMoveSubmenuOpen, setIsMoveSubmenuOpen] = useState(false);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);

  // Delete Dialog State
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleMoreActionsClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsMainMenuOpen(!isMainMenuOpen);
    setIsAssigneeSubmenuOpen(false);
    setIsParentSubmenuOpen(false);
    setIsStoryPointSubmenuOpen(false);
    setIsMoveSubmenuOpen(false);
    setIsSplitDialogOpen(false);
  };

  const handleCloseAll = () => {
    setIsMainMenuOpen(false);
    setIsAssigneeSubmenuOpen(false);
    setIsParentSubmenuOpen(false);
    setIsStoryPointSubmenuOpen(false);
    setIsMoveSubmenuOpen(false);
  };

  const handleAssigneeSubmenu = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsAssigneeSubmenuOpen(!isAssigneeSubmenuOpen);
    setIsParentSubmenuOpen(false);
    setIsStoryPointSubmenuOpen(false);
    setIsMoveSubmenuOpen(false);
  };

  const handleParentSubmenu = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsParentSubmenuOpen(!isParentSubmenuOpen);
    setIsAssigneeSubmenuOpen(false);
    setIsStoryPointSubmenuOpen(false);
    setIsMoveSubmenuOpen(false);
  };

  const handleStoryPointSubmenu = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsStoryPointSubmenuOpen(!isStoryPointSubmenuOpen);
    setIsAssigneeSubmenuOpen(false);
    setIsParentSubmenuOpen(false);
    setIsMoveSubmenuOpen(false);
  };

  const handleMoveSubmenu = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsMoveSubmenuOpen(!isMoveSubmenuOpen);
    setIsAssigneeSubmenuOpen(false);
    setIsParentSubmenuOpen(false);
    setIsStoryPointSubmenuOpen(false);
  };

  const handleAssigneeChange = async (event, newAssignee) => {
    event.stopPropagation();
    event.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await fetch(
        `${API_URL}/api/issues/${issue.id}/assignee`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            assignee: newAssignee === 'Unassigned' ? null : newAssignee
          })
        }
      );

      const data = await response.json();
      
      if (data.status === 'success') {
        if (onAssigneeChange) {
          onAssigneeChange(issue.id, newAssignee);
        }
      } else {
        //console.error('Failed to update assignee:', data.message);
      }
    } catch (error) {
      //console.error('Error updating assignee:', error);
    }
    
    handleCloseAll();
  };

  const handleMenuAction = (event, action, data = null) => {
    event.stopPropagation();
    event.preventDefault();
    
    if (action === 'move') {
      handleMoveSubmenu(event);
      return;
    }
    if (action === 'edit') {
      navigate(`/project/${projectId}/CR`, { 
        state: { 
          editMode: true, 
          issueData: issue 
        } 
      });
      handleCloseAll();
      return;
    }
    if (action === 'delete') {
      setIsDeleteDialogOpen(true);
      handleCloseAll();
      return;
    }
    
    if (onAction) {
      onAction(action, issue, data);
    }
    handleCloseAll();
  };

  const handleDeleteConfirm = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await fetch(
        `${API_URL}/api/issues/${issue.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setIsDeleteDialogOpen(false);
        
        if (onDelete) {
          onDelete(issue.id);
        }
        if (onAction) {
          onAction('delete', issue, { ...data, issueId: issue.id });
        }
        
        handleCloseAll();
      } else {
        if (data.childIssues) {
          setDeleteError({
            type: 'childIssues',
            message: data.message,
            childIssues: data.childIssues
          });
        } else {
          setDeleteError({
            type: 'general',
            message: data.message || 'Failed to delete issue'
          });
        }
      }
    } catch (error) {
      //console.error('Error deleting issue:', error);
      setDeleteError({
        type: 'network',
        message: 'Network error occurred while deleting the issue'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleMoveAction = (issue, destination) => {
    if (onMove) {
      onMove(issue, destination);
    }
    handleCloseAll();
  };

  return (
    <ClickAwayListener onClickAway={handleCloseAll}>
      <Box sx={{ position: 'relative' }}>
        <Box
          onClick={handleMoreActionsClick}
          sx={{
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <MoreVertIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          onClick={(e) => e.stopPropagation()}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: 'error.main' }} />
            Delete Issue
          </DialogTitle>
          <DialogContent>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {deleteError.message}
                {deleteError.type === 'childIssues' && deleteError.childIssues && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      Child issues that would be orphaned:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {deleteError.childIssues.map((child) => (
                        <li key={child.id}>
                          <Typography variant="body2">
                            {child.key}: {child.summary} ({child.issueType})
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this issue?
            </Typography>
            
            <Box sx={{ 
              bgcolor: '#f5f5f5', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {issue.key}: {issue.summary}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Type: {issue.issueType} | Status: {issue.status}
              </Typography>
            </Box>
            
            <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {isDeleting ? 'Deleting...' : 'Delete Issue'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Menu */}
        {isMainMenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              zIndex: 1000,
              minWidth: '200px',
              maxHeight: '400px',
              overflowY: 'auto',
              mt: 0.5,
            }}
          >
            {/* Edit Change Request */}
            <Box
              onClick={(e) => handleMenuAction(e, 'edit')}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Edit Change Request</Typography>
              <ArrowForwardIcon sx={{ fontSize: '14px', color: 'text.disabled' }} />
            </Box>

            <Divider />

            {/* Move work item */}
            <Box
              onClick={(e) => handleMenuAction(e, 'move')}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <MoveIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Move work item</Typography>
              <ArrowForwardIcon sx={{ fontSize: '14px', color: 'text.disabled' }} />
            </Box>

            <Divider />

            {/* Assignee */}
            <Box
              onClick={handleAssigneeSubmenu}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <PersonIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Assignee</Typography>
              <ArrowForwardIcon sx={{ fontSize: '14px', color: 'text.disabled' }} />
            </Box>

            {/* Parent search */}
            <Box
              onClick={handleParentSubmenu}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ParentIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Parent</Typography>
              <ArrowForwardIcon sx={{ fontSize: '14px', color: 'text.disabled' }} />
            </Box>

            {/* Story point estimate */}
            <Box
              onClick={handleStoryPointSubmenu}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <StoryPointIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Story point estimate</Typography>
              <ArrowForwardIcon sx={{ fontSize: '14px', color: 'text.disabled' }} />
            </Box>

            <Divider />

            {/* Split work item */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsSplitDialogOpen(true);
                setIsMainMenuOpen(false);
              }}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <SplitIcon sx={{ fontSize: '16px', mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2">Split work item</Typography>
            </Box>

            {/* Delete */}
            <Box
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                color: 'error.main'
              }}
              onClick={(e) => handleMenuAction(e, 'delete')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon fontSize="small" />
                <Typography variant="body2">Delete Issue</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Submenus */}
        {isMoveSubmenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: '740%',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              zIndex: 1001,
              mr: 1,
              mt: 0.5
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MoveWorkItemMenu
              issue={issue}
              projectKey={projectKey}
              onMove={handleMoveAction}
              onClose={() => setIsMoveSubmenuOpen(false)}
            />
          </Box>
        )}

        {/* Split Work Item Dialog - FIXED with wrapper */}
        <div onClick={(e) => e.stopPropagation()}>
          <SplitWorkItemDialog
            open={isSplitDialogOpen}
            onClose={() => setIsSplitDialogOpen(false)}
            issue={issue}
            projectId={projectId}
            projectDetails={{ key: projectKey }}
            onSplit={(splitData) => {
              if (onAction) {
                onAction('split', issue, splitData);
              }
            }}
          />
        </div>

        {/* Assignee Submenu */}
        {isAssigneeSubmenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: '740%',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              zIndex: 1001,
              minWidth: '280px',
              maxWidth: '320px',
              maxHeight: '400px',
              overflowY: 'auto',
              mr: 1,
              mt: 5.5,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <Typography variant="body2" fontWeight="600" color="text.primary">
                Assign to
              </Typography>
            </Box>

            {/* Unassigned option */}
            <Box
              onClick={(e) => handleAssigneeChange(e, 'Unassigned')}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center',
                backgroundColor: issue.assignee === 'Unassigned' || !issue.assignee ? '#e3f2fd' : 'transparent'
              }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  fontSize: '0.875rem',
                  bgcolor: '#DFE1E6',
                  color: '#172B4D',
                  mr: 2,
                  flexShrink: 0
                }}
              >
                {getInitials('Unassigned')}
              </Avatar>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                Unassigned
              </Typography>
            </Box>

            {teamMembers.map((member, index) => {
              const memberName = member.name || member.username || member.email || `Member ${index + 1}`;
              const memberId = member.employee_id || `${memberName}-${index}`;
              const isSelected = issue.assignee === memberName;
              
              return (
                <Box
                  key={memberId}
                  onClick={(e) => handleAssigneeChange(e, memberName)}
                  sx={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: index < teamMembers.length - 1 ? '1px solid #f0f0f0' : 'none',
                    backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '0.875rem',
                      bgcolor: '#0052CC',
                      color: '#FFFFFF',
                      mr: 2,
                      flexShrink: 0
                    }}
                  >
                    {getInitials(memberName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem', 
                        fontWeight: isSelected ? 600 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {memberName}
                    </Typography>
                    {member.role && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.75rem',
                          color: '#666',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {member.role}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Parent Submenu */}
        {isParentSubmenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: '740%',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              zIndex: 1001,
              mr: 1,
              mt: 10.5
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <HierarchicalParentMenu 
              issue={issue}
              onParentChange={onParentChange}
              onClose={() => setIsParentSubmenuOpen(false)}
            />
          </Box>
        )}

        {/* Story Point Submenu */}
        {isStoryPointSubmenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: '740%',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              zIndex: 1001,
              mr: 1,
              mt: 16.5
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <StoryPointEstimateMenu
              issue={issue}
              onStoryPointChange={(issueId, storyPoints) => {
                if (onAction) {
                  onAction('setStoryPoint', issue, storyPoints);
                }
              }}
              onClose={() => setIsStoryPointSubmenuOpen(false)}
            />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default MoreActionsMenu;